/**
 * Computed Fields Helper Functions
 * 
 * These functions compute optimized fields for cards to improve filtering performance.
 * Fields are computed incrementally when cards are added/updated, avoiding expensive
 * calculations during render.
 */

/**
 * Computes optimized flags for a card based on its collectedCards
 * @param {Array} collectedCards - Array of collected card variations
 * @returns {Object} Computed fields object
 */
export const computeCardFlags = (collectedCards = []) => {
  if (!collectedCards || collectedCards.length === 0) {
    return {
      hasBase: false,
      hasParallels: false,
      hasNumbered: false,
      hasDuplicates: false,
      totalQty: 0,
      parallelNames: []
    };
  }

  let hasBase = false;
  let hasParallels = false;
  let hasNumbered = false;
  let hasDuplicates = false;
  const parallelNamesSet = new Set();
  let totalQty = 0;

  collectedCards.forEach(parallel => {
    const qty = parallel.cardQty || 0;
    totalQty += qty;

    if (parallel.cardParallelName === "BASE") {
      hasBase = true;
    } else {
      hasParallels = true;
    }

    if (parallel.cardPrintNumber) {
      hasNumbered = true;
    }

    if (qty > 1) {
      hasDuplicates = true;
    }

    parallelNamesSet.add(parallel.cardParallelName);
  });

  return {
    hasBase,
    hasParallels,
    hasNumbered,
    hasDuplicates,
    totalQty,
    parallelNames: Array.from(parallelNamesSet)
  };
};

/**
 * Computes searchable fields for a card
 * @param {Object} card - Card object with name and cardNumber
 * @returns {Object} Searchable fields
 */
export const computeSearchableFields = (card) => {
  const searchableName = card.name && Array.isArray(card.name)
    ? card.name.map(n => n.toLowerCase()).join(' ')
    : '';
  
  const searchableCardNumber = card.cardNumber
    ? card.cardNumber.toLowerCase()
    : '';

  return {
    searchableName,
    searchableCardNumber
  };
};

/**
 * Computes all optimized fields for a card
 * @param {Object} card - Card object
 * @returns {Object} Computed fields object (does not include original card data)
 */
export const computeAllCardFields = (card) => {
  if (!card) {
    return {
      hasBase: false,
      hasParallels: false,
      hasNumbered: false,
      hasDuplicates: false,
      totalQty: 0,
      parallelNames: [],
      searchableName: '',
      searchableCardNumber: ''
    };
  }

  const flags = computeCardFlags(card.collectedCards);
  const searchable = computeSearchableFields(card);

  return {
    ...flags,
    ...searchable
  };
};

/**
 * Checks if a card has computed fields (for backward compatibility)
 * @param {Object} card - Card object
 * @returns {boolean} True if card has computed fields
 */
export const hasComputedFields = (card) => {
  return card && (
    card.hasOwnProperty('hasBase') ||
    card.hasOwnProperty('hasParallels') ||
    card.hasOwnProperty('searchableName')
  );
};

/**
 * Ensures a card has computed fields, computing them if missing
 * @param {Object} card - Card object
 * @returns {Object} Card with computed fields added
 */
export const ensureComputedFields = (card) => {
  if (!card) return card;
  
  if (hasComputedFields(card)) {
    return card;
  }

  const computedFields = computeAllCardFields(card);
  return {
    ...card,
    ...computedFields
  };
};
